/**
 * /l/<token>: a secure link from the client's account manager (ACTION_LINKS_PLAN.md). Signed in as the
 * client, a live link goes straight to its page, or a custom link to the site staff sent; anything else
 * (done, expired, withdrawn, someone else's) gets the same answer, so a forwarded link tells the reader
 * nothing.
 */
import { useEffect, useState } from 'react';
import { navigate } from '../lib/router';
import { openActionLink } from '../stores/actionLinks';
import { Spinner } from '../components/ui';
import { Card, H1, PillLink } from './account/kit';

export function ActionLinkPage({ token }: { token: string }) {
  const [state, setState] = useState<'opening' | 'inactive' | 'error'>('opening');
  useEffect(() => {
    let live = true;
    openActionLink(token)
      .then((r) => {
        if (!live) return;
        if (!r) setState('inactive');
        // A custom link: the site staff sent (https, checked when it was sent). It worked once.
        else if (r.external) window.location.replace(r.to);
        else navigate(r.to, { replace: true });
      })
      .catch(() => live && setState('error'));
    return () => {
      live = false;
    };
  }, [token]);
  if (state === 'opening') {
    return (
      <div className="grid flex-1 place-items-center">
        <Spinner />
      </div>
    );
  }
  return (
    <div className="flex flex-1 items-start justify-center p-4 md:p-10">
      <Card className="flex max-w-[560px] flex-col items-start gap-4">
        <H1>{state === 'inactive' ? 'This link is no longer active' : "We couldn't open this link"}</H1>
        <p className="m-0 text-[15px] text-muted">
          {state === 'inactive'
            ? 'It was already used, it expired, or it was replaced by a newer one. Your account manager can send you a new one.'
            : 'Check your connection and try again in a moment.'}
        </p>
        <div className="flex flex-wrap gap-3">
          <PillLink to="/account">Go to my account</PillLink>
          <PillLink to="/account/support" kind="secondary">
            Contact support
          </PillLink>
        </div>
      </Card>
    </div>
  );
}
